import { Box, Theme, Typography, Grid, Dialog, IconButton } from '@mui/material'
import { Form, Formik } from '../../../lib/formik/Formik'
import * as Yup from 'yup'
import { DayJsDatePicker } from '../../onboarding/newDatePicker'
import { MFSubmitButton } from '../../../lib/formik'
import CustomTable from '../../investorOverview/common/customTable'
import { orderHistoryTableHeaderData, ordersPlacedTableHeaderData, orderSummaryTableHeaderData } from './tables/headers'
import { OrderHistoryTableBody, OrderSummaryTableBody } from './tables/body'
import { useState } from 'react'
import { DialogCloseIcon } from '../../../assets/SvgIcons'
import dayjs from '../../../utils/dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { RootStateType } from '../../../redux-store/reducers'
import { OrderHistoryRes, OrderListObj, OrderSummaryRes } from '../../../redux-store/types/orderManagement'
import { getOrderHistoryList, getOrderSummary } from '../../../redux-store/actions/orderManagement'
import { Order, PaginationObj, SortingObj } from '../../investorOverview/types'



export default function OrderSummary(): JSX.Element {
    const dispatch = useDispatch()
    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id)
    const [ordersPlacedOpen, setOrdersPlacedOpen] = useState(false);
    const [orderSummaryTitle, setOrderSummaryTitle] = useState<string>('');
    const [orderSummaryTableData, setOrderSummaryTableData] = useState<OrderSummaryRes[]>([]);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [firstVisit, setFirstVisit] = useState(true);
    const [dateObj, setDateObj] = useState<{ startDate: string, endDate: string }>({ startDate: '', endDate: '' });
    const [tableLoading, setTableLoading] = useState<boolean>(false);
    const [handleOrdersLoading, setHandleOrdersLoading] = useState<boolean>(false);
    const [ordersPlacedTableData, setOrdersPlacedTableData] = useState<OrderListObj[]>([]);
    const [ordersProductType, setOrdersProductType] = useState<string>('');
    const [ordersPlacedSortingObj, setOrdersPlacedSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "ordersPlaced"
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })

    const currentDate = dayjs();

    const validationSchema = Yup.object().shape({
        startDate: Yup
            .date()
            .required('From Date is required')
            .typeError('From Date is required')

            // .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
            .test('min-date', "From Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(currentDate, "From Date should not be greater than today's date")
            .test('max-today', "From Date should not be greater than today's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(currentDate), 'day');
            })

            .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
                // const { endDate } = this.parent;
                // return !endDate || !value || value <= endDate;

                const { endDate } = this.parent;
                if (!endDate || !value) return true;
                return dayjs(value).isSameOrBefore(dayjs(endDate), 'day');
            }),
        endDate: Yup
            .date()
            .required('To Date is required')
            .typeError('To Date is required')

            // .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
            .test('min-date', "To Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(currentDate, "To Date should not be greater than today's date")
            .test('max-today', "To Date should not be greater than today's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(currentDate), 'day');
            })

            .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
                // const { startDate } = this.parent;
                // return !startDate || !value || value >= startDate;

                const { startDate } = this.parent;
                if (!startDate || !value) return true;
                return dayjs(value).isSameOrAfter(dayjs(startDate), 'day');
            }),
    })

    const initialValues = {
        startDate: dayjs(currentDate).format("YYYY-MM-DD"),
        endDate: dayjs(currentDate).format("YYYY-MM-DD"),
    };

    const getISODateRange = (startDate: string, endDate: string) => {
        const ISOStartDate = dayjs(startDate, "YYYY-MM-DD")
            .startOf("day")
            .format("YYYY-MM-DDT00:00:00.000[Z]");

        const today = dayjs().format("YYYY-MM-DD");
        const isToday = dayjs(endDate).format("YYYY-MM-DD") === today;

        const ISOEndDate = isToday
            ? new Date().toISOString() // Get current UTC time
            : dayjs(endDate, "YYYY-MM-DD")
                .endOf("day")
                .format("YYYY-MM-DDT23:59:59.999[Z]");

        return { ISOStartDate, ISOEndDate };
    };

    const handleSubmit = async ({
        startDate,
        endDate
    }: {
        startDate: string;
        endDate: string;
    }) => {
        try {

            setSubmitLoading(true);
            setFirstVisit(false);

            const { ISOStartDate, ISOEndDate } = getISODateRange(startDate, endDate);

            setDateObj({ startDate: ISOStartDate, endDate: ISOEndDate })
            const payload = {
                startDate: ISOStartDate,
                endDate: ISOEndDate,
                rmId: `${iwappRmId}`,
                sort: 'ordersPlaced',
                order: 'desc',
            }
            const orderSummaryRes = await (dispatch(getOrderSummary(payload)) as unknown as Promise<OrderSummaryRes[]>);
            setOrderSummaryTableData(orderSummaryRes);

        } catch (error) {
            console.log('error');
        } finally {
            setSubmitLoading(false);
        }
    }

    const handleOrders = async ({
        sort = "",
        order = "",
        transactionType = [],
        productType = [],
        paymentStatus = [],
        approvalStatus = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
        startDate = dateObj?.startDate ?? "",
        endDate = dateObj?.endDate ?? "",
    }: {
        sort?: string,
        order?: string,
        transactionType?: Array<string>,
        productType?: Array<string>,
        paymentStatus?: Array<string>,
        approvalStatus?: Array<string>,
        searchInput?: string,
        currentPage?: number,
        rowsPerPage?: number,
        startDate?: string,
        endDate?: string
    }) => {
        try {
            setHandleOrdersLoading(true);

            const payload = {
                rmId: `${iwappRmId}`,
                transactionType,
                productType,
                paymentStatus,
                approvalStatus,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage,
                startDate,
                endDate
            }

            const orderPlacedRes = await (dispatch(getOrderHistoryList(payload)) as unknown as Promise<OrderHistoryRes>)

            const { orders, totalCount } = orderPlacedRes;
            setOrdersPlacedTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error');
        } finally {
            setHandleOrdersLoading(false);
        }
    }

    const handleOnClick = (productType: string, header?: string, approvalType?: boolean, paymentType?: boolean, title?: string) => {
        setOrdersPlacedOpen(true);
        setOrderSummaryTitle(title ?? '');
        setOrdersProductType(productType);
        handleOrders({ productType: [productType], approvalStatus: approvalType ? header === 'PLACED' ? [] : [header ?? ""] : [], paymentStatus: paymentType ? [header ?? ""] : [] });
    }


    const handleOrdersPlacedSort = async (orderBy: string, order: Order) => {
        setOrdersPlacedSortingObj({
            order,
            orderBy
        })

        handleOrders({ sort: orderBy, order, productType: [ordersProductType] })

    }
    const handleSort = async (orderBy: string, order: Order) => {
        setTableSortingObj({
            order,
            orderBy
        })
        try {
            setTableLoading(true);
            const payload = {
                startDate: dateObj.startDate,
                endDate: dateObj.endDate,
                rmId: `${iwappRmId}`,
                sort: orderBy,
                order,
            }
            const orderSummaryRes = await (dispatch(getOrderSummary(payload)) as unknown as Promise<OrderSummaryRes[]>);
            setOrderSummaryTableData(orderSummaryRes);
        } catch (error) {
            console.log('error', error);
        } finally {
            setTableLoading(false);
        }
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        handleOrders({ rowsPerPage: Number(event.target.value), productType: [ordersProductType] })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        handleOrders({ currentPage: page + 1, productType: [ordersProductType] })
    }

    const closeOrdersPlacedModal = () => {
        setOrdersPlacedSortingObj({
            order: "desc",
            orderBy: ''
        })

        setTablePaginationObj({
            currentPage: 0,
            rowsPerPage: 10,
            totalRows: 0
        })
        setOrdersPlacedOpen(false);
    }

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values }) => {
                    return (
                        <Form>
                            <Box
                                sx={(theme: Theme) => ({
                                    marginTop: "15.5px",
                                    borderRadius: "25px",
                                    padding: "20px 30px",
                                    backgroundColor: theme.palette.common.white,
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                })}
                            >
                                <Box sx={{ display: { xs: 'block', sm: "flex" }, justifyContent: "space-between", alignItems: 'center' }}>
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "25px",
                                            letterSpacing: "0.01px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        Order Summary
                                    </Typography>

                                    <Box sx={{ display: "flex", alignItems: 'center', columnGap: '10px', mt: { xs: 2, md: 0 } }}>
                                        <Grid container spacing={3} >
                                            <Grid item xs={12} sm={4}>
                                                <DayJsDatePicker
                                                    id=""
                                                    name='startDate'
                                                    label="Start Date"
                                                    maxDate={new Date()}
                                                    disabled={submitLoading}
                                                    resetDate
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <DayJsDatePicker
                                                    id=""
                                                    name='endDate'
                                                    label="End Date"
                                                    minDate={new Date(values.startDate)}
                                                    maxDate={new Date()}
                                                    disabled={submitLoading}
                                                    resetDate
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} sx={{ mt: { xs: 0, sm: 0.9, md: 1.4, lg: 2 } }}>
                                                <MFSubmitButton
                                                    label="Submit"
                                                    loadingEnable={submitLoading}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>

                                {/* Order Summary Table */}
                                {!firstVisit && !submitLoading && (
                                    <Box sx={{ mt: '35px' }}>
                                        <CustomTable
                                            isLoading={tableLoading}
                                            tableBody={
                                                <OrderSummaryTableBody data={orderSummaryTableData ?? []} handleOnClick={handleOnClick} />
                                            }
                                            tableData={orderSummaryTableData ?? []}
                                            tableHeaderData={orderSummaryTableHeaderData}
                                            isPaginationVisible={false}
                                            tableDataCount={(orderSummaryTableData ?? []).length}
                                            handleSort={handleSort}
                                            order={tableSortingObj.order}
                                            orderBy={tableSortingObj.orderBy}
                                            tableBodyLoaderRowCustomStyles={{
                                                '& td:first-of-type': {
                                                    paddingLeft: "0px"
                                                },
                                            }}
                                            tableHeadRowCustomStyles={{
                                                '& th': {
                                                    backgroundColor: '#EDF2F759',
                                                },
                                                '& th:first-of-type': {
                                                    paddingLeft: "0px",
                                                }
                                            }}
                                            tableBodyCustomStyles={{
                                                '& tr:last-child td': {
                                                    border: 0
                                                }
                                            }}
                                            loadingRowsNum={5}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Form>

                    )
                }}
            </Formik>

            {/* Orders Placed Modal */}

            <Dialog
                onClose={() => setOrdersPlacedOpen(false)}
                open={ordersPlacedOpen}
                maxWidth='lg'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "90vw",

                    },
                })}
            >
                <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary,
                                mb: '35px'
                            })}
                        >
                            {`Orders (${orderSummaryTitle})`}
                        </Typography>

                        <IconButton
                            aria-label="close"
                            onClick={closeOrdersPlacedModal}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>

                    <CustomTable
                        isLoading={handleOrdersLoading}
                        tableBody={
                            <OrderHistoryTableBody data={ordersPlacedTableData ?? []} comingFrom="ordersPlaced" />
                        }
                        tableData={ordersPlacedTableData ?? []}
                        tableHeaderData={ordersPlacedTableHeaderData}
                        isPaginationVisible={true}
                        tableDataCount={tablePaginationObj?.totalRows ?? 0}
                        handleSort={handleOrdersPlacedSort}
                        order={ordersPlacedSortingObj.order}
                        orderBy={ordersPlacedSortingObj.orderBy}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        currentPage={tablePaginationObj.currentPage}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th': {
                                backgroundColor: '#EDF2F759',
                            },
                            '& th:first-of-type': {
                                paddingLeft: "0px",
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={5}
                    />
                </Box>

            </Dialog>
        </>
    )
}
